import React, { useState, useMemo, useEffect, useRef } from 'react';
import Icon from '../../../shared/components/Icon';
import UserJourneyCards from '../components/UserJourneyCards';
import UserJourneyCharts from '../components/UserJourneyCharts';
import UserJourneyTable from '../components/UserJourneyTable';
import UserJourneyFilterBuilder from '../components/UserJourneyFilterBuilder';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { getDateRangeFromFilter, getDefaultDateRange } from '../../../shared/utils/dateUtils';

/**
 * UserJourney Main Page
 * Fetches real metrics data from backend API
 */
const UserJourney = ({ onMenuClick, screenSize, selectedPage }) => {
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedSiteIds, setSelectedSiteIds] = useState(() => {
    const storedId = localStorage.getItem("idy");
    return storedId ? [storedId] : [];
  });
  
  // Get teamId from selectedTeamData (which has _id) or fallback to selectedTeam name
  const teamId = useMemo(() => {
    try {
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return selectedTeamData._id || localStorage.getItem("selectedTeam") || "";
    } catch (e) {
      return localStorage.getItem("selectedTeam") || "";
    }
  }, []);
  
  const [metrics, setMetrics] = useState(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  const [websites, setWebsites] = useState([]);
  
  // Filter state
  const [modularFilters, setModularFilters] = useState([]);
  const [appliedModularFilters, setAppliedModularFilters] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(getDefaultDateRange());
  
  // Preview count state (progressive filter preview)
  const [previewCount, setPreviewCount] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const previewTimeoutRef = useRef(null);
  
  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentcolor: "#0f172a",
    borderColor: "#DDE2EE"
  };

  const handleRowClick = (user) => {
    // Toggle expansion: if clicking the same user, collapse; otherwise expand
    setExpandedUserId(expandedUserId === user.userId ? null : user.userId);
  };

  // Fetch websites to get website_id mapping
  useEffect(() => {
    const fetchWebsites = async () => {
      if (!teamId) return;
      
      try {
        const cacheKey = `websites_${teamId}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < 5 * 60 * 1000) {
              setWebsites(data);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse cached websites:', e);
          }
        }

        const isObjectId = typeof teamId === 'string' && /^[a-fA-F0-9]{24}$/.test(teamId);
        let response;
        if (isObjectId) {
          try {
            response = await axiosInstance.get(`/website/team/${teamId}`);
          } catch (err) {
            const teamName = localStorage.getItem('selectedTeam');
            response = await axiosInstance.get(`/website/name/${teamName}`);
          }
        } else {
          const teamName = localStorage.getItem('selectedTeam') || String(teamId);
          response = await axiosInstance.get(`/website/name/${teamName}`);
        }

        if (response.status === 200 && response.data.websites) {
          setWebsites(response.data.websites);
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: response.data.websites,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error('Error fetching websites:', error);
      }
    };

    fetchWebsites();
  }, [teamId]);

  // Get websiteId from selectedSiteIds (use first selected site)
  const websiteId = useMemo(() => {
    if (!selectedSiteIds || selectedSiteIds.length === 0 || websites.length === 0) return null;
    const firstSiteId = selectedSiteIds[0];
    const selectedWebsite = websites.find(w => 
      (w.siteId === firstSiteId) || (w.site_id === firstSiteId)
    );
    return selectedWebsite?.id || selectedWebsite?._id || null;
  }, [selectedSiteIds, websites]);

  // Fetch preview count (progressive filter preview)
  const fetchPreviewCount = async (filtersToPreview) => {
    if (!teamId || !websiteId) {
      setPreviewCount(null);
      return;
    }

    setIsLoadingPreview(true);

    try {
      const dateRange = getDateRangeFromFilter(selectedDateRange);
      
      // Filter out incomplete filters
      const validFilters = filtersToPreview.filter(f => {
        if (!f.field || !f.operator) return false;
        if (f.value === null || f.value === undefined || f.value === '') return false;
        // For multiselect, ensure array is not empty
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        // For between operator, ensure both values are set
        if (f.operator === 'between' && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
        return true;
      });

      const payload = {
        team_id: teamId,
        website_id: websiteId,
        modularFilters: validFilters
      };

      // Add date range if available
      if (dateRange.from) payload.from = dateRange.from;
      if (dateRange.to) payload.to = dateRange.to;

      const response = await axiosInstance.post('/user-identities/filter/preview', payload);
      
      if (response.data && response.data.success) {
        setPreviewCount(response.data.matched_users);
      } else {
        setPreviewCount(null);
      }
    } catch (error) {
      console.error('Error fetching preview count:', error);
      setPreviewCount(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Debounced preview count fetch
  useEffect(() => {
    // Clear existing timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    // Only fetch if modal is open
    if (!filtersOpen) {
      setPreviewCount(null);
      return;
    }

    // Debounce API call (400ms delay)
    previewTimeoutRef.current = setTimeout(() => {
      fetchPreviewCount(modularFilters);
    }, 400);

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [modularFilters, teamId, websiteId, selectedDateRange, filtersOpen]);

  // Fetch total users count when modal opens with no filters
  useEffect(() => {
    if (filtersOpen && modularFilters.length === 0) {
      fetchPreviewCount([]);
    }
  }, [filtersOpen]);

  // Apply modular filters
  const handleApplyModularFilters = () => {
    // Filter out incomplete filters
    const validFilters = modularFilters.filter(f => {
      if (!f.field || !f.operator) return false;
      if (f.value === null || f.value === undefined || f.value === '') return false;
      // For multiselect, ensure array is not empty
      if (Array.isArray(f.value) && f.value.length === 0) return false;
      // For between operator, ensure both values are set
      if (f.operator === 'between' && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      return true;
    });
    
    setAppliedModularFilters(validFilters);
    setFiltersOpen(false);
  };

  // Fetch metrics when teamId, selectedSiteIds, or filters change
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!teamId || !websiteId) {
        setMetrics(null);
        return;
      }

      setIsLoadingMetrics(true);
      setMetricsError(null);

      try {
        const params = {
          team_id: teamId,
          website_id: websiteId
        };

        // Add date range if available
        const dateRange = getDateRangeFromFilter(selectedDateRange);
        if (dateRange.from) params.from = dateRange.from;
        if (dateRange.to) params.to = dateRange.to;

        // Add modular filters if available
        if (appliedModularFilters && appliedModularFilters.length > 0) {
          params.modularFilters = JSON.stringify(appliedModularFilters);
        }

        const response = await axiosInstance.get('/user-journey/metrics', { params });

        if (response.data) {
          setMetrics(response.data);
        }
      } catch (error) {
        console.error('Error fetching user journey metrics:', error);
        setMetricsError(error.response?.data?.error || error.message || 'Failed to load metrics');
        setMetrics(null);
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, [teamId, websiteId, appliedModularFilters, selectedDateRange]);

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto relative">
      <div className="p-4 md:p-6 lg:p-8 space-y-6">

        {/* Stats Cards */}
        {metricsError && (
          <div className="p-4 mb-4 text-center" style={{ 
            backgroundColor: styles.primaryBg, 
            border: `1px solid ${styles.borderColor}`,
            color: styles.textSecondary,
            fontFamily: 'Raleway, sans-serif'
          }}>
            Error loading metrics: {metricsError}
          </div>
        )}
        <UserJourneyCards metrics={metrics} isLoading={isLoadingMetrics} />

        {/* Charts Section */}
        <UserJourneyCharts 
          chartData={metrics ? {
            geographyDistribution: metrics.geographyDistribution,
            commonSources: metrics.commonSources,
            dappsInteracted: metrics.dappsInteracted,
            deviceDistribution: metrics.deviceDistribution
          } : null}
          isLoading={isLoadingMetrics}
          teamId={teamId}
          websiteId={websiteId}
        />

        {/* Users Table */}
        <UserJourneyTable 
          onRowClick={handleRowClick} 
          expandedUserId={expandedUserId}
          teamId={teamId}
          websiteId={websiteId}
          appliedFilters={appliedModularFilters}
          dateRange={selectedDateRange}
        />
      </div>

      {/* Filter Button - Vertical, fixed to right edge */}
      <button
        onClick={() => setFiltersOpen(true)}
        className="fixed z-30 transition-colors shadow-sm flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-[#475569]"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          right: 0,
          top: '16px',
          padding: '16px 8px',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'none',
          border: '1px solid #DDE2EE',
          borderRight: 'none',
          borderRadius: 0
        }}
      >
        <Icon name="filter" size={18} style={{ transform: 'rotate(90deg)' }} />
        <span className="text-sm font-medium">Filters</span>
        {appliedModularFilters.length > 0 && (
          <span className="text-xs opacity-90">•</span>
        )}
      </button>

      {/* Filter Modal */}
      {filtersOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-md transition-opacity"
            onClick={() => setFiltersOpen(false)}
          />
          {/* Filter Modal - Centered with modern styling */}
          <div className="relative bg-white w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-[#DDE2EE]" style={{ borderRadius: 0 }}>
            {/* Modal Header - Modern gradient background */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#DDE2EE] flex-shrink-0 bg-gradient-to-r from-white to-gray-50/50">
              <div>
                <h2 className="text-xl font-semibold text-[#1d0c46]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Filters
                </h2>
                <p className="text-xs text-[#475569] mt-0.5" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Filter user journey data
                </p>
              </div>
              <button
                onClick={() => setFiltersOpen(false)}
                className="p-2 hover:bg-gray-100 text-[#475569] hover:text-[#475569] transition-all duration-200"
                style={{ borderRadius: 0 }}
                title="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Filter Content - Modern scrollable area */}
            <div className="flex-1 overflow-y-auto bg-gray-50/30">
              <div className="p-6">
                <UserJourneyFilterBuilder
                  filters={modularFilters}
                  onChange={setModularFilters}
                  teamId={teamId}
                  websiteId={websiteId}
                  dateRange={getDateRangeFromFilter(selectedDateRange)}
                  selectedSiteIds={selectedSiteIds}
                  onSiteChange={(siteIds) => {
                    setSelectedSiteIds(siteIds);
                    // Store the first selected site ID for backward compatibility
                    if (siteIds.length > 0) {
                      localStorage.setItem("idy", siteIds[0]);
                    } else {
                      localStorage.removeItem("idy");
                    }
                  }}
                />
              </div>
            </div>
            {/* Modal Footer - Modern action buttons */}
            <div className="px-6 py-4 border-t border-[#DDE2EE] flex-shrink-0 flex items-center justify-between bg-white">
              <div className="flex items-center gap-2 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                {modularFilters.length > 0 && (
                  <span className="px-2 py-1 bg-[#1d0c46]/10 text-[#1d0c46] text-xs font-medium" style={{ borderRadius: 0 }}>
                    {modularFilters.length} filter{modularFilters.length !== 1 ? 's' : ''}
                  </span>
                )}
                {isLoadingPreview ? (
                  <span className="text-[#475569]">Calculating...</span>
                ) : previewCount !== null ? (
                  <span className="text-[#475569] font-medium">
                    <span className="font-semibold text-[#1d0c46]">
                      {previewCount.toLocaleString()}
                    </span>
                    {' '}user{previewCount !== 1 ? 's' : ''} match{previewCount === 1 ? 'es' : ''} your filters
                  </span>
                ) : (
                  <span className="text-[#475569]">All users</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setModularFilters([]);
                    setAppliedModularFilters([]);
                    setPreviewCount(null);
                    setFiltersOpen(false);
                  }}
                  className="px-5 py-2.5 text-sm font-medium text-[#475569] hover:text-[#0f172a] border border-[#DDE2EE] hover:border-gray-300 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow"
                  style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                >
                  Reset All
                </button>
                <button
                  onClick={handleApplyModularFilters}
                  className="px-6 py-2.5 text-sm font-semibold bg-[#1d0c46] text-white hover:bg-[#150731] transition-all duration-200 shadow-md hover:shadow-lg"
                  style={{ fontFamily: 'Raleway, sans-serif', borderRadius: 0 }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserJourney;

